import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { FieldType } from "../../lib/types";

/**
 * Sprint 16a — Wrapped API hooks. Read/manage OpenAPI services exposed as MCP
 * tools, and set the current user's per-wrapper credential. Mirrors the
 * `use-routines.ts` idiom (shared `unwrap` + `invalidate`).
 */

export type WrappedApiAuthKind =
  | "bearer"
  | "api_key"
  | "basic"
  | "oauth2_auth_code"
  | "oauth2_client_credentials";

export type WrappedApiAuthScope = "per_user" | "workspace";

export function isOAuthAuthKind(kind: string): boolean {
  return kind === "oauth2_auth_code" || kind === "oauth2_client_credentials";
}

export interface WrappedApiSummary {
  id: string;
  slug: string;
  name: string;
  openapi_spec_url: string | null;
  base_url: string;
  auth_kind: WrappedApiAuthKind;
  auth_kind_scope: WrappedApiAuthScope;
  rate_limit_per_min: number;
  operation_count: number;
  read_only_operation_count: number;
  write_operation_count: number;
  enabled_write_count: number;
  my_credential_set: boolean;
  /** Sprint 16f follow-up: non-secret username of the stored basic-auth credential
   *  (null for other auth kinds / unset). Only populated by get_wrapped_api. */
  my_credential_username: string | null;
  supports_idempotency_replay: boolean;
  idempotency_header: string | null;
  oauth_configured: boolean;
  oauth_client_id: string | null;
  oauth_authorize_url: string | null;
  oauth_token_url: string | null;
  oauth_scopes: string | null;
  spec_last_fetched_at: string | null;
  /** Sprint 16e — subset filter (null dimensions = include all). */
  path_prefix_filter: string | null;
  operation_ids_filter: string[] | null;
  tag_filter: string | null;
  /** Sprint 16e — operations remaining after the subset filter (== operation_count when unfiltered). */
  operation_count_after_filter: number;
  archived: boolean;
  created_at: string;
}

export interface WrappedApiOperation {
  name: string;
  operation_key: string;
  method: string;
  path: string;
  summary: string;
  is_write: boolean;
  enabled: boolean;
}

export interface WrappedApiDetail {
  wrapper: WrappedApiSummary;
  operations: WrappedApiOperation[];
  enabled_operations: string[];
}

export interface WrappedApiCall {
  id: string;
  at: string;
  actor: string;
  actor_host_kind: string | null;
  operation_id: string | null;
  tool_name: string | null;
  method: string | null;
  http_status: number | null;
  latency_ms: number | null;
  retry_count: number;
  idempotency_key: string | null;
  circuit_breaker_state: string | null;
  status: string;
}

export interface WrappedApiHealth {
  wrapper_slug: string;
  circuit_breaker_state: "closed" | "open" | "half_open";
  rate_limit_remaining: number;
  rate_limit_per_min: number;
  /** Sprint 16f: renamed from calls_last_hour; 0 distinguishes "no data yet". */
  call_count_last_hour: number;
  success_rate_last_hour: number | null;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  last_error: { at: string; status: unknown } | null;
}

export type CredentialPayload =
  | { token: string }
  | { key: string; in: "header" | "query"; name: string }
  | { username: string; password: string };

function unwrap<T>(result: {
  isError?: boolean;
  structuredContent?: T;
  content?: Array<{ text?: string }>;
}): T {
  if (result.isError) {
    throw new Error(result.content?.[0]?.text ?? "Request failed");
  }
  return result.structuredContent as T;
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["wrapped-apis"] });
  qc.invalidateQueries({ queryKey: ["wrapped-api"] });
}

export function useWrappedApis(includeArchived = false) {
  return useQuery<WrappedApiSummary[]>({
    queryKey: ["wrapped-apis", includeArchived],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ wrapped_apis: WrappedApiSummary[] }>(
        "list_wrapped_apis",
        includeArchived ? { include_archived: true } : {},
      );
      return unwrap(r)?.wrapped_apis ?? [];
    },
    staleTime: 20_000,
  });
}

export function useWrappedApi(slug: string | undefined) {
  return useQuery<WrappedApiDetail | null>({
    queryKey: slug ? ["wrapped-api", slug] : ["wrapped-api", ""],
    queryFn: async () => {
      if (!slug) return null;
      const r = await mcpClient.toolsCall<WrappedApiDetail>("get_wrapped_api", { slug });
      return unwrap(r) ?? null;
    },
    enabled: !!slug,
    staleTime: 20_000,
  });
}

export interface AddWrappedApiParams {
  slug: string;
  name: string;
  /** Provide a URL (re-fetchable) OR openapi_spec_json (pasted, frozen). */
  openapi_spec_url?: string;
  /** Sprint 16d — pasted spec text for private/internal specs with no public URL. */
  openapi_spec_json?: string;
  auth_kind: WrappedApiAuthKind;
  auth_kind_scope?: WrappedApiAuthScope;
  base_url?: string;
  rate_limit_per_min?: number;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  oauth_authorize_url?: string;
  oauth_token_url?: string;
  oauth_scopes?: string;
  supports_idempotency_replay?: boolean;
  idempotency_header?: string;
  // Sprint 16e — subset filter (all optional; AND-combined).
  path_prefix?: string;
  operation_ids?: string[];
  tag?: string;
}

export interface AddWrappedApiResult {
  wrapper: WrappedApiSummary;
  operation_count: number;
  read_only_operation_count: number;
  write_operation_count: number;
  /** Sprint 16e — operations wrapped after the subset filter. */
  operation_count_after_filter: number;
  new_tools: string[];
}

export function useAddWrappedApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddWrappedApiParams): Promise<AddWrappedApiResult> => {
      const r = await mcpClient.toolsCall<AddWrappedApiResult>(
        "add_wrapped_api",
        params as unknown as Record<string, unknown>,
      );
      return unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export interface UpdateWrappedApiParams {
  slug: string;
  name?: string;
  openapi_spec_url?: string;
  /** Sprint 16d — replace the spec from pasted text (URL-less wrappers). */
  openapi_spec_json?: string;
  base_url?: string;
  rate_limit_per_min?: number;
  enabled_operations?: string[];
  auth_kind_scope?: WrappedApiAuthScope;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  oauth_authorize_url?: string;
  oauth_token_url?: string;
  oauth_scopes?: string;
  supports_idempotency_replay?: boolean;
  idempotency_header?: string;
  // Sprint 16e — subset filter. Pass a value to set it, "" / [] to clear it, omit to leave unchanged.
  path_prefix?: string;
  operation_ids?: string[];
  tag?: string;
}

export function useUpdateWrappedApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpdateWrappedApiParams): Promise<WrappedApiSummary> => {
      const r = await mcpClient.toolsCall<{ wrapper: WrappedApiSummary }>(
        "update_wrapped_api",
        params as unknown as Record<string, unknown>,
      );
      return unwrap(r).wrapper;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useRefreshWrappedApiSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<{ operation_count: number; new_tools: string[] }> => {
      const r = await mcpClient.toolsCall<{ operation_count: number; new_tools: string[] }>(
        "refresh_wrapped_api_spec",
        { slug },
      );
      return unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useResetCircuitBreaker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (wrapperSlug: string): Promise<{ ok: boolean }> => {
      const r = await mcpClient.toolsCall<{ ok: boolean }>("reset_wrapped_api_circuit_breaker", {
        wrapper_slug: wrapperSlug,
      });
      return unwrap(r);
    },
    onSuccess: (_d, slug) => {
      qc.invalidateQueries({ queryKey: ["wrapped-api-health", slug] });
    },
  });
}

export function useSetWrappedApiServiceCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      wrapper_slug: string;
      credential_payload: CredentialPayload;
    }): Promise<{ ok: boolean }> => {
      const r = await mcpClient.toolsCall<{ ok: boolean }>("set_wrapped_api_service_credential", {
        wrapper_slug: params.wrapper_slug,
        credential_payload: params.credential_payload as unknown as Record<string, unknown>,
      });
      return unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

/** Call log — polls while the tab is open (Sprint 16b S16B.28). */
export function useWrappedApiCalls(
  slug: string | undefined,
  opts: { statusFilter?: string; enabled?: boolean } = {},
) {
  return useQuery<WrappedApiCall[]>({
    queryKey: ["wrapped-api-calls", slug, opts.statusFilter ?? "all"],
    queryFn: async () => {
      if (!slug) return [];
      const r = await mcpClient.toolsCall<{ calls: WrappedApiCall[] }>("list_wrapped_api_calls", {
        wrapper_slug: slug,
        limit: 100,
        ...(opts.statusFilter ? { status_filter: opts.statusFilter } : {}),
      });
      return unwrap(r)?.calls ?? [];
    },
    enabled: !!slug && opts.enabled !== false,
    refetchInterval: opts.enabled === false ? false : 5000,
  });
}

/** Health summary — polls every 10s while the page is open (Sprint 16b S16B.30). */
export function useWrappedApiHealth(slug: string | undefined, enabled = true) {
  return useQuery<WrappedApiHealth | null>({
    queryKey: ["wrapped-api-health", slug],
    queryFn: async () => {
      if (!slug) return null;
      const r = await mcpClient.toolsCall<WrappedApiHealth>("get_wrapped_api_health", {
        wrapper_slug: slug,
      });
      return unwrap(r) ?? null;
    },
    enabled: !!slug && enabled,
    refetchInterval: enabled ? 10000 : false,
  });
}

export function useRemoveWrappedApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<{ removed_tool_count: number }> => {
      const r = await mcpClient.toolsCall<{ removed_tool_count: number }>("remove_wrapped_api", { slug });
      return unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useSetWrappedApiCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      wrapper_slug: string;
      credential_payload: CredentialPayload;
    }): Promise<{ ok: boolean }> => {
      const r = await mcpClient.toolsCall<{ ok: boolean }>("set_wrapped_api_credential", {
        wrapper_slug: params.wrapper_slug,
        credential_payload: params.credential_payload as unknown as Record<string, unknown>,
      });
      return unwrap(r);
    },
    onSuccess: () => invalidate(qc),
  });
}

/** Sprint 16e — subset filter inputs (chrome-side, before normalisation). */
export interface SubsetFilterInput {
  path_prefix?: string;
  operation_ids?: string[];
  tag?: string;
}

export interface SubsetFilterPreview {
  total_operations: number;
  filtered_operations_count: number;
  available_tags: string[];
  sample_operations: Array<{
    method: string;
    path: string;
    operation_id: string | null;
    operation_key: string;
    tags: string[];
  }>;
}

/**
 * Sprint 16e — live preview for the "Filter operations" UI. Calls the owner-only
 * `preview_wrapped_api_filter` tool (server-side fetch + parse + count, so the
 * 1.6 MB spec never touches the browser). Debounce the inputs upstream via the
 * `enabled` flag + a stable query key; only runs when a spec URL is present.
 */
export function useFilterPreview(
  specUrl: string | undefined,
  filter: SubsetFilterInput,
  enabled = true,
) {
  const key = JSON.stringify({ specUrl, filter });
  return useQuery<SubsetFilterPreview | null>({
    queryKey: ["wrapped-api-filter-preview", key],
    queryFn: async () => {
      if (!specUrl?.trim()) return null;
      const r = await mcpClient.toolsCall<SubsetFilterPreview>("preview_wrapped_api_filter", {
        openapi_spec_url: specUrl.trim(),
        ...(filter.path_prefix?.trim() ? { path_prefix: filter.path_prefix.trim() } : {}),
        ...(filter.operation_ids && filter.operation_ids.length ? { operation_ids: filter.operation_ids } : {}),
        ...(filter.tag?.trim() ? { tag: filter.tag.trim() } : {}),
      });
      return unwrap(r) ?? null;
    },
    enabled: enabled && !!specUrl?.trim(),
    staleTime: 60_000,
    retry: false,
  });
}

/** Parse a comma/space/newline-separated operation_ids string into a trimmed list. */
export function parseOperationIdsInput(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Sprint 16c — response→entity mappings (THE MOAT)
// ---------------------------------------------------------------------------

export type SyncMode = "live" | "cache" | "hybrid";
export const SYNC_MODES: readonly SyncMode[] = ["live", "cache", "hybrid"];

export interface FkRule {
  wrapped_field: string;
  target_entity_type: string;
  matches_entity_field: string;
  /** Reference field on the materialised entity that receives the resolved ID. */
  fk_field: string;
}

export interface ScopeFilter {
  wrapped_query_param?: string;
  post_filter_field?: string;
}

export interface WrappedApiMapping {
  id: string;
  wrapper_slug: string;
  operation_id: string;
  target_entity_type_slug: string;
  field_map: Record<string, string>;
  fk_resolution: FkRule[];
  sync_mode: SyncMode;
  cache_ttl_seconds: number;
  scope_filter: ScopeFilter | null;
  collection_path: string | null;
  external_id_field: string | null;
  external_timestamp_field: string | null;
  summary_template: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  archived: boolean;
}

export interface MappingInput {
  wrapper_slug: string;
  operation_id: string;
  target_entity_type_slug: string;
  field_map: Record<string, string>;
  fk_resolution?: FkRule[];
  sync_mode?: SyncMode;
  cache_ttl_seconds?: number;
  scope_filter?: ScopeFilter | null;
  collection_path?: string | null;
  external_id_field?: string | null;
  external_timestamp_field?: string | null;
  summary_template?: string | null;
}

export type MappingPatch = Partial<Omit<MappingInput, "wrapper_slug" | "operation_id">> & {
  wrapper_slug: string;
  operation_id: string;
};

function invalidateMappings(qc: ReturnType<typeof useQueryClient>, slug?: string) {
  qc.invalidateQueries({ queryKey: ["wrapped-api-mappings"] });
  if (slug) qc.invalidateQueries({ queryKey: ["wrapped-api-mappings", slug] });
}

export function useWrappedApiMappings(wrapperSlug: string | undefined) {
  return useQuery<WrappedApiMapping[]>({
    queryKey: ["wrapped-api-mappings", wrapperSlug ?? ""],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ mappings: WrappedApiMapping[] }>(
        "list_wrapped_api_mappings",
        wrapperSlug ? { wrapper_slug: wrapperSlug } : {},
      );
      return unwrap(r)?.mappings ?? [];
    },
    enabled: !!wrapperSlug,
    staleTime: 15_000,
  });
}

export function useAddWrappedApiMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MappingInput): Promise<WrappedApiMapping> => {
      const r = await mcpClient.toolsCall<{ mapping: WrappedApiMapping }>(
        "add_wrapped_api_mapping",
        input as unknown as Record<string, unknown>,
      );
      return unwrap(r).mapping;
    },
    onSuccess: (_d, input) => invalidateMappings(qc, input.wrapper_slug),
  });
}

export function useUpdateWrappedApiMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: MappingPatch): Promise<WrappedApiMapping | null> => {
      const r = await mcpClient.toolsCall<{ mapping: WrappedApiMapping | null }>(
        "update_wrapped_api_mapping",
        patch as unknown as Record<string, unknown>,
      );
      return unwrap(r).mapping;
    },
    onSuccess: (_d, patch) => invalidateMappings(qc, patch.wrapper_slug),
  });
}

export function useRemoveWrappedApiMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { wrapper_slug: string; operation_id: string }): Promise<{ ok: boolean }> => {
      const r = await mcpClient.toolsCall<{ ok: boolean }>("remove_wrapped_api_mapping", params);
      return unwrap(r);
    },
    onSuccess: (_d, params) => invalidateMappings(qc, params.wrapper_slug),
  });
}

// ---------------------------------------------------------------------------
// Sprint 16d — AI-assisted setup wizard
//
// The wizard brain (`wrap_api_from_openapi`) and the two helper tools run the
// edge model server-side and return structured proposals; this is the chrome
// contract for those returns plus thin react-query wrappers. The proposals are
// pre-filled into the wizard steps as editable defaults.
// ---------------------------------------------------------------------------

export type Confidence = "high" | "medium" | "low";
export type RecommendedState = "enabled" | "review" | "disabled";

export interface WizardOperationProposal {
  operation_id: string;
  method: string;
  path: string;
  summary: string;
  recommended_state: RecommendedState;
  confidence: Confidence;
  reason: string;
}

export interface WizardMappingProposal {
  operation_id: string;
  /** Existing entity-type slug, or the sentinel "new". */
  target_entity_type_slug: string;
  field_map: Record<string, string>;
  sync_mode: SyncMode;
  confidence: Confidence;
  reason: string;
}

export interface OAuthDiscovery {
  discovered: boolean;
  source?: string;
  authorize_url?: string;
  token_url?: string;
  scopes_supported?: string[];
}

export interface WrapApiProposal {
  proposed_slug: string;
  proposed_name: string;
  detected_auth_kind: WrappedApiAuthKind;
  auth_confidence: Confidence;
  base_url: string | null;
  oauth_hint: { detected: boolean; flow?: "auth_code" | "client_credentials"; scopes?: string[] } | null;
  operations: WizardOperationProposal[];
  mapping_proposals: WizardMappingProposal[];
  oauth_discovery: OAuthDiscovery | null;
}

/** Compact entity-type view passed to the wizard brain for mapping proposals. */
export interface EntityTypeSummaryInput {
  slug: string;
  name: string;
  fields: { name: string; type: string }[];
}

export interface WrapApiFromOpenapiParams {
  openapi_spec_url?: string;
  openapi_spec_json?: string;
  existing_entity_types?: EntityTypeSummaryInput[];
}

export function useWrapApiFromOpenapi() {
  return useMutation({
    mutationFn: async (params: WrapApiFromOpenapiParams): Promise<WrapApiProposal> => {
      const r = await mcpClient.toolsCall<WrapApiProposal>(
        "wrap_api_from_openapi",
        params as unknown as Record<string, unknown>,
      );
      return unwrap(r);
    },
  });
}

export interface EntityTypeFieldProposal {
  name: string;
  type: FieldType;
  display_field?: boolean;
}

export interface EntityTypeProposal {
  proposed_type_slug: string;
  proposed_name: string;
  proposed_fields: EntityTypeFieldProposal[];
}

export function useSuggestEntityTypeForResponse() {
  return useMutation({
    mutationFn: async (params: {
      wrapper_slug: string;
      operation_id: string;
      sample_response?: unknown;
    }): Promise<EntityTypeProposal> => {
      const r = await mcpClient.toolsCall<EntityTypeProposal>(
        "suggest_entity_type_for_wrapped_response",
        params as unknown as Record<string, unknown>,
      );
      return unwrap(r);
    },
  });
}

export function useInferOAuthConfig() {
  return useMutation({
    mutationFn: async (baseUrl: string): Promise<OAuthDiscovery> => {
      const r = await mcpClient.toolsCall<OAuthDiscovery>("infer_oauth_config", { base_url: baseUrl });
      return unwrap(r);
    },
  });
}

export interface IntegrationSuggestion {
  category: string;
  matched_types: string[];
  suggested_apis: { name: string; slug: string; openapi_spec_url?: string }[];
  benefit: string;
}

/**
 * Onboarding hints — calls the hidden, rule-based `suggest_wrapped_api_integrations`
 * with the workspace's entity-type + wrapper slugs (passed by the caller, which
 * already has them loaded).
 */
export function useWrappedApiIntegrationSuggestions(
  typeSlugs: string[],
  wrapperSlugs: string[],
  enabled = true,
) {
  return useQuery<IntegrationSuggestion[]>({
    queryKey: ["wrapped-api-integration-suggestions", [...typeSlugs].sort().join(","), [...wrapperSlugs].sort().join(",")],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ suggestions: IntegrationSuggestion[] }>(
        "suggest_wrapped_api_integrations",
        { installed_type_slugs: typeSlugs, existing_wrapper_slugs: wrapperSlugs },
      );
      return unwrap(r)?.suggestions ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
}

/** A live test call against a synthesised wrapper tool (Step 5 + Step 4 sample fetch). */
export async function callWrapperTool(toolName: string, args: Record<string, unknown> = {}): Promise<{
  ok: boolean;
  body: unknown;
  error?: string;
}> {
  try {
    const r = await mcpClient.toolsCall<unknown>(toolName, args);
    if (r.isError) {
      return { ok: false, body: null, error: r.content?.[0]?.text ?? "call failed" };
    }
    // Wrapper tools return the upstream JSON as structuredContent (preferred) or text.
    const body =
      r.structuredContent !== undefined
        ? r.structuredContent
        : (() => {
            const text = r.content?.[0]?.text ?? "";
            try {
              return JSON.parse(text);
            } catch {
              return text;
            }
          })();
    return { ok: true, body };
  } catch (err) {
    return { ok: false, body: null, error: err instanceof Error ? err.message : "call failed" };
  }
}
